import api from './api';
import type { PlatformInvoicePrint } from './billingService';

// ============================================
// TYPES
// ============================================

export interface Plan {
    _id: string;
    name: string;
    description?: string;
    monthlyPrice: number;
    yearlyPrice: number;
    yearlyDiscount: number;
    features: string[];
    isPopular: boolean;
    sortOrder: number;
    isActive: boolean;
    createdBy?: { _id: string; name: string; email: string };
    createdAt: string;
    updatedAt: string;
}

export interface Subscription {
    _id: string;
    schoolId: { _id: string; name: string; email: string; phone?: string; address?: string; logo?: string; isActive: boolean } | string;
    planId: { _id: string; name: string; features: string[]; monthlyPrice: number; yearlyPrice: number } | string;
    billingCycle: 'monthly' | 'yearly';
    status: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended' | 'pending_payment';
    startDate: string;
    endDate: string;
    trialEndsAt?: string;
    autoRenew: boolean;
    createdBy?: { _id: string; name: string; email: string };
    createdAt: string;
    updatedAt: string;
}

export interface Payment {
    _id: string;
    schoolId: { _id: string; name: string; email: string; address?: string; phone?: string } | string;
    subscriptionId: string;
    planId: { _id: string; name: string; monthlyPrice: number; yearlyPrice: number } | string;
    amount: number;
    currency: string;
    billingCycle: 'monthly' | 'yearly';
    paymentMethod: 'bank_transfer' | 'cash' | 'cheque' | 'online';
    paymentDate: string;
    referenceNumber?: string;
    notes?: string;
    invoiceNumber: string;
    markedBy: { _id: string; name: string } | string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SubscriptionStats {
    totalPlans: number;
    totalSubscriptions: number;
    expiringSoon: number;
    monthlyRevenue: number;
    statusBreakdown: Record<string, number>;
    planBreakdown: Record<string, number>;
}

export interface SubscriptionHistoryItem {
    _id: string;
    subscriptionId: string;
    schoolId: string;
    action: 'created' | 'plan_changed' | 'billing_cycle_changed' | 'extended' | 'suspended' | 'resumed' | 'cancelled' | 'expired' | 'renewed';
    previousPlanId?: { _id: string; name: string };
    newPlanId?: { _id: string; name: string };
    previousBillingCycle?: 'monthly' | 'yearly';
    newBillingCycle?: 'monthly' | 'yearly';
    previousEndDate?: string;
    newEndDate?: string;
    previousStatus?: string;
    newStatus?: string;
    notes?: string;
    performedBy: { _id: string; name: string; email: string };
    createdAt: string;
}

// My Subscription response (for current user's school)
export interface MySubscription {
    isSystemAdmin?: boolean;
    hasFullAccess?: boolean;
    subscriptionId?: string;
    status?: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended' | 'pending_payment';
    startDate?: string;
    endDate?: string;
    billingCycle?: 'monthly' | 'yearly';
    trialEndsAt?: string;
    daysRemaining?: number;
    isExpired?: boolean;
    plan?: {
        id: string;
        name: string;
        features: string[];
        monthlyPrice?: number;
        yearlyPrice?: number;
    };
}

// Feature type
export interface Feature {
    key: string;
    label: string;
}

// Cache for features (fetched from API)
let cachedFeatures: Feature[] | null = null;

/**
 * Get available features from API (single source of truth).
 * Results are cached to avoid repeated API calls.
 */
export const getAvailableFeatures = async (): Promise<Feature[]> => {
    if (cachedFeatures) {
        return cachedFeatures;
    }
    
    try {
        const response = await api.get('/subscriptions/features');
        cachedFeatures = response.data.data;
        return cachedFeatures || [];
    } catch (error) {
        console.error('Failed to fetch features:', error);
        // Fallback if the API fails — the same list as the initial render.
        return AVAILABLE_FEATURES;
    }
};

/**
 * Clear the features cache (useful if features are updated).
 */
export const clearFeaturesCache = () => {
    cachedFeatures = null;
};

// Initial render fallback before the API call resolves, and the list used
// when it fails. Stays in lockstep with backend/src/constants/features.ts →
// FEATURE_LABELS.
export const AVAILABLE_FEATURES = [
    { key: 'admissions',        label: 'Admissions' },
    { key: 'academic_sessions', label: 'Academic Sessions' },
    { key: 'attendance',        label: 'Attendance' },
    { key: 'timetable',         label: 'Timetable' },
    { key: 'examination',       label: 'Examination' },
    { key: 'analysis',          label: 'Analysis' },
    { key: 'reports',           label: 'Reports' },
    { key: 'letters',           label: 'Letters' },
    { key: 'leaves',            label: 'Leave Management' },
    { key: 'question_bank',     label: 'Question Bank' },
    { key: 'expenses',          label: 'Expenses' },
    { key: 'events',            label: 'Events' },
    { key: 'policies',          label: 'Policies' },
    { key: 'sops',              label: 'SOPs' },
    { key: 'fees',              label: 'Fees' },
    { key: 'id_cards',          label: 'ID Cards' },
    { key: 'email',             label: 'Email' },
];

// ============================================
// MY SUBSCRIPTION API (For current user)
// ============================================

export const getMySubscription = async (): Promise<MySubscription> => {
    const response = await api.get('/subscriptions/my-subscription');
    return response.data.data;
};

// ============================================
// PLANS API - PUBLIC
// ============================================

export const getPublicPlans = async (): Promise<Plan[]> => {
    const response = await api.get('/subscriptions/plans/public');
    return response.data.data;
};

// ============================================
// PLANS API - ADMIN
// ============================================

export const getAllPlans = async (): Promise<Plan[]> => {
    const response = await api.get('/subscriptions/plans');
    return response.data.data;
};

export const getPlanById = async (id: string): Promise<Plan> => {
    const response = await api.get(`/subscriptions/plans/${id}`);
    return response.data.data;
};

export const createPlan = async (data: {
    name: string;
    description?: string;
    monthlyPrice: number;
    yearlyPrice: number;
    yearlyDiscount?: number;
    features: string[];
    isPopular?: boolean;
    sortOrder?: number;
}): Promise<Plan> => {
    const response = await api.post('/subscriptions/plans', data);
    return response.data.data;
};

export const updatePlan = async (id: string, data: Partial<Plan>): Promise<Plan> => {
    const response = await api.put(`/subscriptions/plans/${id}`, data);
    return response.data.data;
};

export const deletePlan = async (id: string): Promise<void> => {
    await api.delete(`/subscriptions/plans/${id}`);
};

// ============================================
// SUBSCRIPTIONS API
// ============================================

export const getAllSubscriptions = async (params?: {
    status?: string;
    planId?: string;
    search?: string;
    page?: number;
    limit?: number;
}): Promise<{ data: Subscription[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => {
    const response = await api.get('/subscriptions/subscriptions', { params });
    return { data: response.data.data, pagination: response.data.pagination };
};

export const getSubscriptionById = async (id: string): Promise<Subscription> => {
    const response = await api.get(`/subscriptions/subscriptions/${id}`);
    return response.data.data;
};

export const getSubscriptionBySchool = async (schoolId: string): Promise<Subscription> => {
    const response = await api.get(`/subscriptions/subscriptions/school/${schoolId}`);
    return response.data.data;
};

export const updateSubscription = async (id: string, data: Partial<Subscription>): Promise<Subscription> => {
    const response = await api.put(`/subscriptions/subscriptions/${id}`, data);
    return response.data.data;
};

export const cancelSubscription = async (id: string): Promise<Subscription> => {
    const response = await api.delete(`/subscriptions/subscriptions/${id}`);
    return response.data.data;
};

export const suspendSubscription = async (id: string, reason?: string): Promise<Subscription> => {
    const response = await api.post(`/subscriptions/subscriptions/${id}/suspend`, { reason });
    return response.data.data;
};

export const resumeSubscription = async (id: string): Promise<Subscription> => {
    const response = await api.post(`/subscriptions/subscriptions/${id}/resume`);
    return response.data.data;
};

export const getSubscriptionHistory = async (
    schoolId: string,
    params?: { page?: number; limit?: number }
): Promise<{ data: SubscriptionHistoryItem[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => {
    const response = await api.get(`/subscriptions/history/school/${schoolId}`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
};

// ============================================
// PAYMENTS API
// ============================================

export const recordPayment = async (data: {
    schoolId: string;
    subscriptionId: string;
    amount: number;
    currency?: string;
    billingCycle?: 'monthly' | 'yearly';
    paymentMethod: 'bank_transfer' | 'cash' | 'cheque' | 'online';
    paymentDate: string;
    referenceNumber?: string;
    notes?: string;
    extendSubscription?: boolean;
}): Promise<Payment> => {
    const response = await api.post('/subscriptions/payments', data);
    return response.data.data;
};

export const getPaymentsBySchool = async (
    schoolId: string,
    params?: { page?: number; limit?: number }
): Promise<{ data: Payment[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> => {
    const response = await api.get(`/subscriptions/payments/school/${schoolId}`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
};

/** Any school's payment invoice (platform admins), laid out for the PDF drawn in the browser. */
export const getInvoice = async (paymentId: string): Promise<PlatformInvoicePrint> => {
    const response = await api.get(`/subscriptions/payments/${paymentId}/invoice`);
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data;
};

// ============================================
// STATISTICS API
// ============================================

export const getSubscriptionStats = async (): Promise<SubscriptionStats> => {
    const response = await api.get('/subscriptions/subscriptions/stats');
    return response.data.data;
};

const subscriptionService = {
    // Features
    getAvailableFeatures,
    clearFeaturesCache,
    // My Subscription
    getMySubscription,
    // Public
    getPublicPlans,
    // Plans
    getAllPlans,
    getPlanById,
    createPlan,
    updatePlan,
    deletePlan,
    // Subscriptions
    getAllSubscriptions,
    getSubscriptionById,
    getSubscriptionBySchool,
    updateSubscription,
    cancelSubscription,
    suspendSubscription,
    resumeSubscription,
    // Subscription History
    getSubscriptionHistory,
    // Payments
    recordPayment,
    getPaymentsBySchool,
    getInvoice,
    // Stats
    getSubscriptionStats,
};

export default subscriptionService;
